import { useCallback } from "react"

import {
  prefetchCustomerRepositoryArticlesOfIncorporationRetrieveQuery,
  useCustomerCompanyPartialUpdate,
  useCustomerCompanyRetrieveSuspense,
} from "@boundless/bl-api-client/customer/services"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { z } from "zod"

import { ArticlesOfIncorporationForm } from "@/components/articles-of-incorporation/ArticlesOfIncorporationForm"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { CompanyFlowContext } from "@/lib/flows/company"

export const Route = createFileRoute(
  "/_dashboard/company/articles-of-incorporation",
)({
  component: ArticlesOfIncorporationView,
  validateSearch: z.object({
    from: z.string().optional(),
  }),
  beforeLoad: ({ context: { store, hasPermissions } }) => {
    if (!hasPermissions(["repository.view_repositorydocument"])) {
      throw redirect({ to: "/" })
    }
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }
    return { companyUuid }
  },
  loader: async ({ context: { companyUuid, queryClient } }) => {
    await prefetchCustomerRepositoryArticlesOfIncorporationRetrieveQuery(
      queryClient,
      companyUuid,
    )
  },
})

function ArticlesOfIncorporationView() {
  const { companyUuid } = Route.useRouteContext()
  const queryClient = useQueryClient()
  const { data: company, queryKey: companyQueryKey } =
    useCustomerCompanyRetrieveSuspense(companyUuid)

  const flowActorRef = CompanyFlowContext.useActorRef()
  const goNext = () => flowActorRef.send({ type: "next" })
  const goBack = () => flowActorRef.send({ type: "back" })

  const { mutate: updateCompany } = useCustomerCompanyPartialUpdate({
    mutation: {
      onSuccess: (updatedCompanyData) => {
        queryClient.setQueryData(companyQueryKey, updatedCompanyData)
        goNext()
      },
    },
  })

  const handleSkip = useCallback(() => {
    if (company.skippedArticlesOfIncorporation) {
      goNext()
    } else {
      // Update the company to indicate that link was skipped, and
      // trigger goNext (as part of the onSuccess callback)
      updateCompany({
        uuid: companyUuid,
        data: { skippedArticlesOfIncorporation: true },
      })
    }
  }, [companyUuid, updateCompany, company.skippedArticlesOfIncorporation])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Articles of Incorporation</CardTitle>
      </CardHeader>
      <CardContent>
        <ArticlesOfIncorporationForm
          companyUuid={companyUuid}
          onNext={goNext}
          onBack={goBack}
          onSkip={handleSkip}
        />
      </CardContent>
    </Card>
  )
}
